import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Clock, CheckCircle2, Check } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const Contact = () => {
  useSEO(
    'Kontakt & Booking | Få et Tilbud | House of Falafel',
    'Planlægger du fest eller event? Kontakt House of Falafel for uforpligtende tilbud på mobil catering. Ring på 22907030 eller udfyld vores bookingformular!'
  );

  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-40 pb-20 md:pt-48 lg:pt-56 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/76e41030-ec38-42da-9e63-2e6acd74222c/82f5e037-ccf1-4e47-a10c-b765ce6a6696.png" 
            alt="House of Falafel Food Truck" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left md:text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-black mb-6"
          >
            Kontakt & <span className="text-amber-500">Booking</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-zinc-400 max-w-2xl mx-0 md:mx-auto"
          >
            Planlægger du et event? Vi står klar til at servere autentisk street food til dine gæster. Udfyld formularen eller ring til os direkte.
          </motion.p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Info Column */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-bold mb-8">Direkte kontakt</h2>
                <div className="space-y-6">
                  <a href="tel:22907030" className="flex items-center gap-6 group">
                    <Phone className="text-white w-6 h-6 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-zinc-500 uppercase tracking-wider font-bold">Ring til os</p>
                      <p className="text-xl font-bold group-hover:text-amber-500 transition-colors">22 90 70 30</p>
                    </div>
                  </a>

                  <a href="mailto:houseoffalafel2023@icloud.com" className="flex items-center gap-6 group">
                    <Mail className="text-white w-6 h-6 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-zinc-500 uppercase tracking-wider font-bold">Send en mail</p>
                      <p className="text-xl font-bold group-hover:text-amber-500 transition-colors">houseoffalafel2023@icloud.com</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-6">
                    <MapPin className="text-white w-6 h-6 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-zinc-500 uppercase tracking-wider font-bold">Kørselsområde</p>
                      <p className="text-xl font-bold">Vi kører i Jylland og Fyn</p>
                      <p className="text-sm text-zinc-400 mt-1">Sjælland mod ekstra betaling</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-12 border-t border-zinc-900">
                <h3 className="text-xl font-bold mb-6">Hvorfor vælge os?</h3>
                <ul className="space-y-4">
                  {[
                    "Frisklavet mad direkte fra vognen",
                    "Hjemmelavede opskrifter og råvarer",
                    "Erfaring med alt fra 20 til 500+ gæster",
                    "Hurtig og professionel servering",
                    "Fleksible menuer tilpasset dit budget"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-zinc-400">
                      <Check className="text-white w-5 h-5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-zinc-900/50 p-8 rounded-3xl border border-zinc-800">
                <div className="flex items-center gap-4 mb-4">
                  <Clock className="text-amber-500 w-6 h-6" />
                  <h3 className="text-xl font-bold">Svartid</h3>
                </div>
                <p className="text-zinc-400">
                  Vi bestræber os på at besvare alle henvendelser inden for 24 timer på hverdage. Ved akutte forespørgsler anbefaler vi at ringe direkte.
                </p>
              </div>
            </div>

            {/* Booking Form Column */}
            <div className="bg-zinc-900/80 backdrop-blur-md p-8 md:p-12 rounded-3xl border border-white/10 shadow-2xl">
              <h2 className="text-3xl font-bold mb-2">Book catering</h2>
              <p className="text-zinc-400 mb-8">Udfyld formularen for et uforpligtende tilbud.</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot */}
                <div className="sr-only absolute">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Navn</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      placeholder="Dit fulde navn"
                      className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Telefon</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      placeholder="Dansk telefonnummer"
                      className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all" 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    required 
                    placeholder="din@email.dk"
                    className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all" 
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Eventtype</label>
                    <select 
                      name="event_type" 
                      className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all appearance-none"
                    >
                      <option>Firmafest</option>
                      <option>Fødselsdag</option>
                      <option>Konfirmation</option>
                      <option>Festival</option>
                      <option>Bryllup</option>
                      <option>Andet</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Antal gæster</label>
                    <input 
                      type="number" 
                      name="guests" 
                      placeholder="F.eks. 50"
                      className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all" 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Besked / Detaljer</label>
                  <textarea 
                    name="message" 
                    rows="4" 
                    placeholder="Fortæl os lidt om dit event (dato, lokation, ønsker til menu osv.)"
                    className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-all resize-none"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-amber-600 hover:bg-amber-700 hover:-translate-y-1 text-white py-4 rounded-xl font-bold transition-all shadow-lg shadow-amber-600/20 flex items-center justify-center gap-2"
                >
                  Send forespørgsel <Send className="w-5 h-5" />
                </button>

                {status === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-500/10 border border-green-500/50 rounded-xl text-green-500 text-center font-medium"
                  >
                    Tak for din forespørgsel! Vi vender tilbage hurtigst muligt.
                  </motion.div>
                )}
                {status === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-center font-medium"
                  >
                    Der opstod en fejl. Prøv venligst at ringe til os i stedet.
                  </motion.div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Info */}
      <footer className="py-12 border-t border-zinc-900 text-center text-zinc-500 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="mb-2">House of Falafel · CVR: 44081652</p>
          <p>© {new Date().getFullYear()} Alle rettigheder forbeholdes.</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
